import {
  URL_ADAF,
  URL_ADDRESS_PAGE,
  URL_USER_OFFERS,
  URL_USER_ORGANIZATION_TEAM,
  URL_SUBSCRIPTION,
  URL_USER_NOTIFICATIONS,
  URL_USER_PROFILE_MANAGE,
  URL_OFFER_QUESTION,
} from '@tools/routes/routes';
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useModal } from '@composables/useModal.ts';
import type { HelpMenuLinks, HelpMenuSubmenu } from '@global-types/nav-v2/helpMenuItem';
import type { Url } from '@global-types/url';
import { useAppDataStore } from '@stores/useAppDataStore';

export const useNavbarStore = defineStore('navbar', () => {
  const { userNotifications: notifications } = useAppDataStore();
  const helpMenuData = ref<HelpMenuLinks>(window.NEW_HELP_MENU);

  const urlExpanded: string[] = [
    URL_USER_NOTIFICATIONS,
    URL_USER_PROFILE_MANAGE,
    URL_USER_OFFERS,
    URL_SUBSCRIPTION,
    URL_ADAF,
    URL_ADDRESS_PAGE,
    URL_USER_ORGANIZATION_TEAM,
  ] as const;

  const urlExpandedPatterns: RegExp[] = [
    /^\/spaces\/\d+$/,
    /^\/organization\/business_input\/\d{6}$/,
  ] as const;

  const enableScrollHidePatterns: RegExp[] = [
    /^\/location-bureau-[^/?#]+\/?$/i, // Landing page offices
    /^\/coworking-[^/?#]+\/?$/i, // Landing page coworking
    /^\/recherche(?:\/)?\?.+/i, // Search page
  ];

  const isExpanded = ref<boolean>(false);
  const currentDropdownOpen = ref<string | null>(null);

  const setCurrentDropdownOpen = (newCurrentDropdownOpen: string | null): void => {
    currentDropdownOpen.value = newCurrentDropdownOpen;
  };

  const toggleExpanded = (): void => {
    isExpanded.value = !isExpanded.value;

    setCurrentDropdownOpen(null);
  };

  const isCurrentPage = (link: string | undefined): boolean => window.location.pathname === link;

  const {
    isOpen: isPlusMenuOpen,
    toggleModal: togglePlusMenu,
    closeModal: closePlusMenu,
  } = useModal();

  const isCurrentDropdownOpen = (dropdown: string): boolean => currentDropdownOpen.value === dropdown;

  const hiddenMobileNavbarRoutes: string[] = [
    URL_OFFER_QUESTION,
  ] as const;

  const hiddenMobileNavbarRoutePatterns: RegExp[] = [
    /^\/O-[A-Z0-9]{6}(?:-[^/]+)*\/?$/, // offer page, regex for hash_id - name
    /^\/offer\/[^/]+\/[^/]+\/completion\/\d+\/?$/, // first step offer contact
    /^\/offer\/[^/]+\/[^/]+\/schedule\/\d+\/?$/, // second step offer contact
  ] as const;

  const isMobileNavbarVisible = computed<boolean>(() => {
    const { pathname } = window.location;

    const isHiddenExact = hiddenMobileNavbarRoutes.includes(pathname);
    const isHiddenPattern = hiddenMobileNavbarRoutePatterns.some((pattern) => pattern.test(pathname));

    return !(isHiddenExact || isHiddenPattern);
  });

  const shouldEnableScrollHide = (): boolean => {
    const { pathname, search } = window.location;
    const fullPath = `${pathname}${search}`;
    return enableScrollHidePatterns.some((pattern) => pattern.test(fullPath));
  };

  const setExpanded = (expanded: boolean): void => {
    isExpanded.value = expanded;
  };

  const initExpanded = (): void => {
    const { pathname } = window.location;

    const isExactMatch = urlExpanded.includes(pathname);
    const isPatternMatch = urlExpandedPatterns.some((pattern) => pattern.test(pathname));

    if (isExactMatch || isPatternMatch) {
      setExpanded(true);
    }
  };

  initExpanded();

  const whatsappLink = computed<Url | null>(() => helpMenuData.value.whatsappLink);
  const recalledSubmenus = computed<HelpMenuSubmenu[]>(() => helpMenuData.value.recalledSubmenus);
  const faqLink = computed<Url | null>(() => helpMenuData.value.intercomFaqLink);
  const recallLink = computed<Url | null>(() => helpMenuData.value.recallLink);
  const salesPhone = computed<string | null>(() => helpMenuData.value.salesPhone);

  return {
    isExpanded,
    setExpanded,
    toggleExpanded,
    isCurrentPage,
    notifications,
    isPlusMenuOpen,
    togglePlusMenu,
    closePlusMenu,
    currentDropdownOpen,
    setCurrentDropdownOpen,
    isCurrentDropdownOpen,
    isMobileNavbarVisible,
    shouldEnableScrollHide,
    whatsappLink,
    recalledSubmenus,
    faqLink,
    recallLink,
    salesPhone,
  };
});
